/**
 * Who the meeting is with (BUILD-SPEC.md §6). This is the riskiest part of
 * the build: every failure mode is expected, not exceptional, and all of
 * them collapse to the same outcome — an empty tick list plus a message,
 * never a blank screen or a blocked start.
 */
import * as microsoftTeams from "@microsoft/teams-js";
import { getGraphToken } from "./auth";

export interface RosterMember {
  userId: string;
  displayName: string;
}

export type MemberFetchResult =
  | { ok: true; members: RosterMember[] }
  | { ok: false; reason: string };

export const EMPTY_ROSTER_MESSAGE =
  "could not read who this meeting is with — add names below";

const DEV_FAIL_PARAM = "simulateRosterFailure";

/**
 * Dev/test hook so the empty-roster fallback path is easy to exercise
 * without needing to actually break AAD consent, go offline, etc.
 * (BUILD-SPEC.md §6: "Build it so this path is exercised in development,
 * not discovered in production.") Append `?simulateRosterFailure=1` (or any
 * non-empty value) to the app URL.
 */
function devForcedFailureReason(): string | null {
  if (typeof window === "undefined") return null;
  const flag = new URLSearchParams(window.location.search).get(DEV_FAIL_PARAM);
  return flag ? "simulated failure (dev flag)" : null;
}

/**
 * Fetches the chat's member list. Every branch below is a *handled* failure
 * that resolves to `{ ok: false, reason }` — nothing here throws or
 * rejects. Callers map `ok: false` straight to the empty tick list plus
 * `EMPTY_ROSTER_MESSAGE`.
 */
export async function fetchMembers(): Promise<MemberFetchResult> {
  const forced = devForcedFailureReason();
  if (forced) return { ok: false, reason: forced };

  let chatId: string | undefined;
  try {
    const context = await microsoftTeams.app.getContext();
    chatId = context.chat?.id;
  } catch (err) {
    return { ok: false, reason: `could not read Teams context: ${describeError(err)}` };
  }

  if (!chatId) {
    // Ad-hoc call with no chat, or a surface that doesn't expose one.
    return { ok: false, reason: "no chat id available for this meeting" };
  }

  let token: string;
  try {
    token = await getGraphToken();
  } catch (err) {
    // Consent refused, popup blocked, tenant needs admin approval, offline
    // before the popup even opens, etc — all the same outcome.
    return { ok: false, reason: `could not get consent to read members: ${describeError(err)}` };
  }

  try {
    const res = await fetch(
      `https://graph.microsoft.com/v1.0/chats/${encodeURIComponent(chatId)}/members`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res.ok) {
      return { ok: false, reason: `Graph returned ${res.status}` };
    }
    const body = (await res.json()) as { value?: unknown[] };
    const members = (body.value ?? [])
      .map(toRosterMember)
      .filter((m): m is RosterMember => m !== null);
    return { ok: true, members };
  } catch (err) {
    return { ok: false, reason: `network error reading members: ${describeError(err)}` };
  }
}

function toRosterMember(raw: unknown): RosterMember | null {
  if (typeof raw !== "object" || raw === null) return null;
  const record = raw as Record<string, unknown>;
  const userId =
    typeof record["userId"] === "string"
      ? record["userId"]
      : typeof record["id"] === "string"
        ? record["id"]
        : null;
  const displayName = typeof record["displayName"] === "string" ? record["displayName"] : null;
  if (!userId || !displayName) return null;
  return { userId, displayName };
}

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
